import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { YoutubeLogo, InstagramLogo, Clock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const PLATFORM_META = {
  youtube: { icon: YoutubeLogo, color: "#EAB308", label: "YouTube" },
  instagram: { icon: InstagramLogo, color: "#00E5FF", label: "Instagram" },
};

export default function Settings() {
  const { user, logout } = useAuth();
  const [oauth, setOauth] = useState(null);

  useEffect(() => {
    api.get("/oauth/status").then(r => setOauth(r.data)).catch(() => {});
  }, []);

  const connect = async (platform) => {
    try {
      const { data } = await api.get(`/oauth/${platform}/connect`);
      if (data.status === "coming_soon") {
        toast.info(`${PLATFORM_META[platform].label} connection coming soon`, { description: data.message });
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="settings-page">
      <p className="text-[#FF3B6B] text-sm font-mono">// SETTINGS</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Profile & account</h1>

      <div className="mt-8 glass rounded-xl p-6 space-y-4">
        <div>
          <p className="text-xs text-white/50 uppercase tracking-wider">Name</p>
          <p className="font-medium mt-1">{user?.name}</p>
        </div>
        <div>
          <p className="text-xs text-white/50 uppercase tracking-wider">Email</p>
          <p className="font-medium mt-1">{user?.email}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wider">Plan</p>
            <p className="font-medium mt-1 capitalize text-[#FF3B6B]">{user?.plan}</p>
          </div>
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wider">Credits</p>
            <p className="font-medium mt-1 font-mono">{user?.credits}</p>
          </div>
        </div>
        <Link to="/pricing" className="inline-block btn-primary px-5 py-2.5 rounded-lg text-sm font-medium mt-4" data-testid="upgrade-plan-link">Upgrade plan</Link>
      </div>

      {/* Connected accounts */}
      <h2 className="mt-12 text-lg font-semibold">Connected accounts</h2>
      <p className="text-sm text-white/50 mt-1">Direct publishing requires platform OAuth approval (multi-week process). The Chrome extension covers the manual workflow today.</p>
      <div className="mt-4 space-y-3" data-testid="oauth-section">
        {oauth && Object.entries(oauth).map(([platform, info]) => {
          const meta = PLATFORM_META[platform];
          return (
            <div key={platform} className="glass rounded-xl p-4 flex items-center gap-4" data-testid={`oauth-${platform}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${meta.color}15`, color: meta.color }}>
                <meta.icon weight="fill" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{meta.label}</p>
                <p className="text-xs text-white/50 mt-0.5">
                  {info.connected ? `Connected as ${info.handle}` :
                    info.configured ? "Click to connect" :
                    `Coming soon · approval typically ${info.approval_typical_time}`}
                </p>
              </div>
              {info.connected ? (
                <span className="flex items-center gap-1 text-xs text-[#00E5FF] font-mono"><CheckCircle weight="fill" size={14} /> Connected</span>
              ) : (
                <button
                  onClick={() => connect(platform)}
                  className="px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1"
                  style={info.configured ? { background: meta.color, color: "#fff" } : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}
                  data-testid={`oauth-connect-${platform}`}
                >
                  {info.configured ? "Connect" : <><Clock size={12} /> Soon</>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={logout} className="mt-8 text-sm text-white/50 hover:text-white" data-testid="settings-logout-btn">Sign out</button>
    </div>
  );
}
